/**
 * PageHeader - Reusable page title molecule used across all top-level pages.
 *
 * Renders a consistent page heading with:
 *	 - Title in Lulo font (uppercase, tracked, brand accent)
 *	 - Optional subtitle below the title
 *	 - Optional action button (e.g., "Add Building", "New Order") aligned right
 *
 * The button includes a "+" icon by default but can be hidden via showButtonIcon.
 *
 * Usage:
 *	 <PageHeader
 *		 title="Buildings"
 *		 subtitle="Manage service locations and project sites."
 *		 buttonLabel="Add Building"
 *		 onButtonClick={() => navigate('/admin?tab=locations')}
 *	 />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface PageHeaderProps {
	/** Page title text — rendered in Lulo heading font, uppercased automatically */
	title: string;
	/** Optional subtitle shown below the title in muted text */
	subtitle?: string;
	/** Label for the optional action button (button hidden if omitted) */
	buttonLabel?: string;
	/** Click handler for the action button */
	onButtonClick?: () => void;
	/** Whether to show the "+" icon inside the button (default: true) */
	showButtonIcon?: boolean;
	/**
	 * Optional secondary action rendered to the left of the primary button.
	 * Styled as a quiet outline button so the primary create CTA stays
	 * visually dominant. Used for things like "Generate PDF" on list pages.
	 */
	secondaryActionLabel?: string;
	/** Click handler for the secondary action button */
	onSecondaryActionClick?: () => void;
	/** Disables the secondary action (e.g. while a PDF is rendering) */
	secondaryActionDisabled?: boolean;
	/** Optional extra className for the outer wrapper */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const PageHeader: React.FC<PageHeaderProps> = ({
	title,
	subtitle,
	buttonLabel,
	onButtonClick,
	showButtonIcon = true,
	secondaryActionLabel,
	onSecondaryActionClick,
	secondaryActionDisabled = false,
	className = '',
}) => {
	return (
		<div className={`page-header flex flex-col tablet:flex-row tablet:flex-wrap tablet:items-start tablet:justify-between mb-24px gap-12px ${className}`} data-ref="molecule-page-header">
			{/* --- Title + subtitle block --- */}
			<div className="page-header__titles min-w-0">
				{/* Title — Lulo heading font, uppercase, brand accent color */}
				<h1 className="page-header__title text-20px tablet:text-24px font-heading uppercase tracking-wide text-brand-heading">
					{title}
				</h1>

				{/* Subtitle — smaller muted text below title */}
				{subtitle && (
					<p className="page-header__subtitle mt-4px text-14px text-brand-text-muted font-sans">
						{subtitle}
					</p>
				)}
			</div>

			{/* --- Action buttons (secondary then primary) --- */}
			{/* shrink-0 keeps the buttons at their natural size and lets the row
			    wrap instead — on a 768px iPad the sidebar takes 219px, and a
			    long title beside a two-button cluster used to push the page
			    sideways rather than dropping the buttons onto their own line. */}
			<div className="page-header__actions flex shrink-0 flex-col tablet:flex-row gap-8px w-full tablet:w-auto">
				{/* Quiet outline button — sits to the left of the primary CTA.
				    Used for "Generate PDF" etc. so the create button stays the
				    visually dominant action on the page. */}
				{secondaryActionLabel && onSecondaryActionClick && (
					<button
						type="button"
						onClick={onSecondaryActionClick}
						disabled={secondaryActionDisabled}
						className="inline-flex items-center gap-6px px-16px py-10px bg-white text-brand-text-muted border border-brand-border rounded-8px text-12px font-heading uppercase tracking-wide hover:bg-brand-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary min-h-44px transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full tablet:w-auto justify-center"
					>
						{secondaryActionLabel}
					</button>
				)}

				{/* Primary CTA — usually "+ New X" on list pages */}
				{buttonLabel && onButtonClick && (
					<button
						type="button"
						onClick={onButtonClick}
						className="inline-flex items-center gap-6px px-16px py-10px bg-brand-primary text-white rounded-8px text-12px font-heading uppercase tracking-wide hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary min-h-44px transition-colors shadow-sm w-full tablet:w-auto justify-center"
					>
						{/* Plus icon — shown by default, can be hidden */}
						{showButtonIcon && (
							<svg className="w-14px h-14px" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
							</svg>
						)}
						{buttonLabel}
					</button>
				)}
			</div>
		</div>
	);
};
